/**
 * PATH: apps/quote/src/components/builder/unified-bottom-bar/SheetSwitcher.tsx
 * PURPOSE: Sheet tab switcher with scrollable section tabs
 */

import { useRef, useCallback } from 'react'
import { Plus, Layers, Calculator, ChevronLeft, ChevronRight, FileSpreadsheet, Settings } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useWorkspace } from '../BuilderWorkspace'
import type { QuoteSection } from '@/lib/quoteApi'

interface SheetSwitcherProps {
  sections: QuoteSection[]
  onAddSection: () => void
  isAddingSectionPending?: boolean
}

function Tab({ id, label, isActive, onClick, icon: Icon, variant = 'section' }: {
  id: string; label: string; isActive: boolean; onClick: () => void
  icon?: React.ComponentType<{ className?: string }>; variant?: 'section' | 'special'
}) {
  return (
    <button onClick={onClick} className={cn(
      'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-t whitespace-nowrap transition-colors',
      isActive
        ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white border-t border-x border-slate-200 dark:border-slate-700 -mb-px'
        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-slate-800/50',
      variant === 'special' && !isActive && 'text-slate-400 dark:text-slate-500'
    )}>
      {Icon && <Icon className="w-3 h-3" />}
      <span className="max-w-[80px] truncate">{label}</span>
    </button>
  )
}

export function SheetSwitcher({ sections, onAddSection, isAddingSectionPending }: SheetSwitcherProps) {
  const { activeSheet, activateSection, activateOtherCharges, activateAssumptions, activateSummary } = useWorkspace()
  const scrollRef = useRef<HTMLDivElement>(null)
  const scrollTabs = useCallback((direction: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: direction === 'left' ? -150 : 150, behavior: 'smooth' })
  }, [])

  return (
    <div className="flex items-center gap-0.5 min-w-0 flex-1 justify-center">
      <button onClick={() => scrollTabs('left')} className="flex-shrink-0 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded"><ChevronLeft className="w-3.5 h-3.5" /></button>
      <div ref={scrollRef} className="flex items-end gap-0.5 overflow-x-auto scrollbar-hide min-w-0 max-w-full px-1" style={{ scrollBehavior: 'smooth' }}>
        {sections.map((section) => (
          <Tab key={section.id} id={section.id} label={section.title} isActive={activeSheet.type === 'section' && activeSheet.sectionId === section.id} onClick={() => activateSection(section.id)} icon={Layers} />
        ))}
        <button onClick={onAddSection} disabled={isAddingSectionPending} className={cn('flex-shrink-0 p-1 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800', isAddingSectionPending && 'opacity-50 cursor-wait')} title="Add section"><Plus className="w-3.5 h-3.5" /></button>
        <div className="w-px h-4 bg-slate-300 dark:bg-slate-600 mx-1 flex-shrink-0" />
        <Tab id="other-charges" label="Other" isActive={activeSheet.type === 'otherCharges'} onClick={activateOtherCharges} icon={Calculator} variant="special" />
        {/* Tab labelled "Setup" (was "Config") — same target sheet, clearer entry-point name */}
        {/* per estimator feedback: "setup is missing" — they didn't realise Config was setup. */}
        <Tab id="assumptions" label="Setup" isActive={activeSheet.type === 'assumptions'} onClick={activateAssumptions} icon={Settings} variant="special" />
        <Tab id="summary" label="Summary" isActive={activeSheet.type === 'summary'} onClick={activateSummary} icon={FileSpreadsheet} variant="special" />
      </div>
      <button onClick={() => scrollTabs('right')} className="flex-shrink-0 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded"><ChevronRight className="w-3.5 h-3.5" /></button>
    </div>
  )
}
